import React, { useEffect } from 'react'; 
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import * as LoginAction from 'actions/login-action';
import { connect } from 'react-redux';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ToastHandler from 'components/Toast-Handler/toast-handler';
import TagManager from 'react-gtm-module';
import { bindActionCreators } from 'redux';
import backgroundimg from './background.png';
import PageHeader from 'global/PageHeader';
import { tokens } from 'theme/tokens';

TagManager.dataLayer({
  dataLayer: {
    event: 'pageview',
    pagePath: '/login',
    pageTitle: 'login',
  },
});

const LoginPage = (props) => {
  const { toast, isProcessing, loggedUser } = props;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  // Move styles outside component using styled components
  const RootGrid = styled('div')(({ backgroundimg }) => ({
    minHeight: '100vh',
    width: '100vw',
    backgroundImage: `url("${backgroundimg}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  const ContainerGrid = styled('div')(({ theme }) => ({
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    width: '30%',
    maxWidth: 900,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'absolute',
    right: theme.spacing(16),
    bottom: theme.spacing(16),
    [theme.breakpoints.down('md')]: {
      position: 'static',
      flexDirection: 'column',
      width: '95%',
      minHeight: 'auto',
      boxShadow: theme.shadows[1],
      right: 'auto',
      bottom: 'auto',
      margin: theme.spacing(2, 'auto'),
    },
  }));

  const LeftSection = styled('div')(({ theme }) => ({
    flex: 1.75,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafafa',
    height: '100%', // Ensure it fills ContainerGrid height
    [theme.breakpoints.down('md')]: {
      minHeight: 180,
      maxHeight: 220,
      flex: 1,
    },
  }));

  const RightSection = styled('div')(({ theme }) => ({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(4),
    minHeight: 400,
    background: '#fff',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2),
      minHeight: 320,
      flex: 1,
    },
  }));

  const StyledImage = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover', // Fill the parent and crop if needed
  });

  const StyledAvatar = styled(Avatar)(() => ({
    margin: 'auto',
    backgroundColor: colors.secondary.main,
  }));  

  useEffect(() => {
    if (loggedUser && loggedUser.message === '200 OK' && loggedUser.response.token) {
      new Promise(r => setTimeout(r, 2000));
      const businessModel = localStorage.getItem('CT_BUSINESS_MODEL');
      navigate('/app/home', { replace: true });
    }
  }, [loggedUser, navigate]);

  const handleLogin = (evt) => {
    evt.preventDefault();
    const formElements = evt.target.elements;
    const user = {};
    user.email = formElements['email'].value;
    user.password = formElements['password'].value;
    props.loginUser(user); //call login action
  };

  return (
    <RootGrid backgroundimg={backgroundimg}>
      <CssBaseline />
      <ContainerGrid>
        {/* Left Section: Image */}
        {/* {!isMobile && (
          <LeftSection>
            <StyledImage src={tmsImg} alt='img' />
          </LeftSection>
        )} */}
        {/* Right Section: Login */}
        <RightSection>
          <PageHeader
            title={'Sign In'}
            subTitle={'Sign In with your valid email & password.'}
          />
          <br />
          <StyledAvatar>
            <LockOutlinedIcon />
          </StyledAvatar>
          <br />
          <Formik
            initialValues={{
              email: '',
              password: ''
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string().email('Must be a valid email').max(255).required('Email is required'),
              password: Yup.string().max(255).required('Password is required')
            })}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleLogin}>
                {isProcessing && <CircularProgress />}
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label='Email Address'
                  margin='normal'
                  name='email'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='email'
                  value={values.email}
                  variant='outlined'
                />
                <TextField
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  label='Password'
                  margin='normal'
                  name='password'
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type='password'
                  value={values.password}
                  variant='outlined'
                />
                <Box my={2}>
                  <Button
                    color='primary'
                    disabled={isSubmitting || !values.password || !values.email}
                    fullWidth
                    //size='large'
                    type='submit'
                    variant='contained'
                    sx={{ width: '100%' }}
                  >
                    Log In
                  </Button>
                </Box>
                <Typography
                  color='textSecondary'
                  variant='body1'
                >
                  Don&apos;t have an account?
                  {' '}
                  <Link component={RouterLink} to='https://freightsmart.ai/#pricing' variant='body1' underline="hover">
                    Sign up
                  </Link>
                  {' OR '}
                  <Link component={RouterLink} to='/recover' variant='body1' underline="hover">
                    Forgot Password?
                  </Link>
                </Typography>
                <div style={{ minHeight: 34 }}>
                  <ToastHandler component='LoginPage'
                    toast={toast || { message: '' }}
                    type={toast ? toast.type : 'INFO'} />
                </div>
              </form>
            )}
          </Formik>
        </RightSection>
      </ContainerGrid>
    </RootGrid>
  );
};

const mapStateToProps = state => {
  const { loggedUser, isProcessing, toast } = state.loginReducer;
  return {
    loggedUser,
    isProcessing,
    toast
  };
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators(LoginAction, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);